'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { Dispatch, PropsWithChildren, SetStateAction, createContext, use, useContext, useEffect } from 'react';
import { useLocalStorage, useSessionStorage } from 'usehooks-ts';
import { HTC_ACCESS_TOKEN_KEY } from '@/constants/auth';
import { deriveAccountType, extractUsernameFromEmail, getHtcAccountId } from '@/lib/auth';
import { getClientCookie } from '@/lib/client-cookie';
import { AppConfig } from '@/types/configs';
import { GUEST_SESSION_STORE_KEY, GuestSessionStore } from '@/types/session-store';
import { HUBS_STORE_LOCAL_KEY, HubsStoreState } from '@/types/store';
type ConfigsContextType = {
  store: HubsStoreState | null;
  setStore: Dispatch<SetStateAction<HubsStoreState>>;
  sessionStore: GuestSessionStore | null;
  setSessionStore: Dispatch<SetStateAction<GuestSessionStore>>;
  removeStore: () => void;
  appConfig: AppConfig;
  accountType: 'wallet_account' | 'htc_account' | 'guest';
  accountId: string;
  isCreateDomain: boolean;
};
const ConfigsContext = createContext<ConfigsContextType | null>(null);
type ConfigsProviderProps = PropsWithChildren<{
  appConfigPromise: Promise<AppConfig>;
  isCreateDomainPromise: Promise<boolean>;
}>;
export const ConfigsProvider = t0 => {
  const $ = _c(29);
  const {
    children,
    appConfigPromise,
    isCreateDomainPromise
  } = t0;
  const appConfig = use(appConfigPromise);
  const isCreateDomain = use(isCreateDomainPromise);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [store, setStore, removeLocalStore] = useLocalStorage(HUBS_STORE_LOCAL_KEY, t1);
  let t2;
  if ($[1] !== removeLocalStore) {
    t2 = () => {
      try {
        removeLocalStore();
      } catch {}
    };
    $[1] = removeLocalStore;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const removeStore = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [sessionStore, setSessionStore] = useSessionStorage(GUEST_SESSION_STORE_KEY, t3);
  const credentials = store?.credentials;
  const viveportAuth = store?.viveportAuth;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getHtcAccountId(getClientCookie(HTC_ACCESS_TOKEN_KEY));
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const cookieAccountId = t4;
  let t5;
  if ($[5] !== credentials?.accountId || $[6] !== credentials?.email) {
    t5 = cookieAccountId || credentials?.accountId || extractUsernameFromEmail(credentials?.email ?? "");
    $[5] = credentials?.accountId;
    $[6] = credentials?.email;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const accountId = t5;
  let t6;
  if ($[8] !== viveportAuth) {
    t6 = viveportAuth ?? {};
    $[8] = viveportAuth;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = deriveAccountType(t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const accountType = t7;
  const searchParams = useSearchParams();
  let t8;
  let t9;
  if ($[12] !== appConfig || $[13] !== searchParams) {
    t8 = () => {
      if (searchParams.get("debug") === "true") {
        window._WEB_CONFIGS_ = appConfig;
      }
    };
    t9 = [searchParams, appConfig];
    $[12] = appConfig;
    $[13] = searchParams;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== accountId || $[17] !== accountType || $[18] !== appConfig || $[19] !== isCreateDomain || $[20] !== removeStore || $[21] !== sessionStore || $[22] !== setSessionStore || $[23] !== setStore || $[24] !== store) {
    t10 = {
      store,
      setStore,
      removeStore,
      sessionStore,
      setSessionStore,
      appConfig,
      accountType,
      accountId,
      isCreateDomain
    };
    $[16] = accountId;
    $[17] = accountType;
    $[18] = appConfig;
    $[19] = isCreateDomain;
    $[20] = removeStore;
    $[21] = sessionStore;
    $[22] = setSessionStore;
    $[23] = setStore;
    $[24] = store;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== children || $[27] !== t10) {
    t11 = <ConfigsContext.Provider value={t10}>{children}</ConfigsContext.Provider>;
    $[26] = children;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};
export const useConfigsContext = () => {
  const value = useContext(ConfigsContext);
  if (value == null) {
    throw new Error("useConfigsContext cannot be used outside of ConfigsProvider");
  }
  return value;
};